// src/components/MapboxGL/controls/BiomassLegendControl.js

/**
 * Control tipo leyenda para mostrar metadatos de biomasa del GeoJSON.
 * Se inspira visualmente en TreeStatsBarControl, pero solo presenta los
 * campos de biomasa relevantes.
 */
class BiomassLegendControl {
  constructor(options = {}) {
    this.appBarHeight = options.appBarHeight || '0px'
    this.visible = options.visible !== false
    this.options = this._normalizeMetadata(options.metadata || options.meta || options)
  }

  onAdd(map) {
    this._map = map

    this._wrapper = document.createElement('div')
    this._wrapper.className = 'biomass-legend-ctrl'
    Object.assign(this._wrapper.style, {
      position: 'absolute',
      top: `calc(${this.appBarHeight} + 62px)`,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '1',
      pointerEvents: 'auto',
    })

    this._shadow = this._wrapper.attachShadow({ mode: 'open' })
    this._renderShell()
    this._renderContent()

    map.getContainer().appendChild(this._wrapper)

    this._dummy = document.createElement('div')
    return this._dummy
  }

  onRemove() {
    if (this._wrapper?.parentNode) this._wrapper.parentNode.removeChild(this._wrapper)
    if (this._dummy?.parentNode) this._dummy.parentNode.removeChild(this._dummy)
    this._map = null
  }

  updateMetadata(metadata) {
    this.options = this._normalizeMetadata(metadata)
    this._renderContent()
  }

  setVisible(visible) {
    this.visible = Boolean(visible)
    this._applyVisibility()
  }

  _applyVisibility() {
    if (!this._wrapper) return
    this._wrapper.style.display = this.visible ? 'block' : 'none'
  }

  _renderShell() {
    const style = document.createElement('style')
    style.textContent = `
    :host { all: initial; }

    .legend {
      display: grid;
      gap: 8px;
      box-sizing: border-box;
      width: min(820px, calc(100vw - 24px));
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      backdrop-filter: blur(8px);
      padding: 8px;
      font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
      font-size: 0.72rem;
      font-weight: 500;
      color: #111;
      background: rgba(255, 255, 255, 0.58);
      border: 1px solid rgba(17,24,39,0.10);
    }

    @media (prefers-color-scheme: dark) {
      .legend {
        background: rgba(25, 25, 30, 0.78);
        color: #f0f0f0;
        border-color: rgba(255,255,255,0.12);
      }
    }

    .legend-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 1px 2px 0;
    }

    .title {
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 0;
      text-transform: uppercase;
      opacity: 0.78;
    }

    .count {
      display: flex;
      align-items: baseline;
      justify-content: center;
      gap: 6px;
      flex: 0 0 auto;
      padding: 4px 9px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.55);
      border: 1px solid rgba(17,24,39,0.10);
      box-shadow: 0 1px 4px rgba(0,0,0,0.10);
      backdrop-filter: blur(4px);
      color: #111;
    }

    @media (prefers-color-scheme: dark) {
      .count {
        background: rgba(40, 40, 46, 0.78);
        color: #f0f0f0;
        border-color: rgba(255,255,255,0.12);
      }
    }

    .count .label {
      font-size: 0.68rem;
      opacity: .76;
    }

    .count .num {
      font-size: 0.86rem;
    }

    .metrics {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 8px;
    }

    .metric-card {
      --accent: #16a34a;
      --tint: rgba(187, 247, 208, 0.48);
      --icon-bg: rgba(187, 247, 208, 0.95);
      --icon-color: #14532d;
      display: grid;
      grid-template-rows: auto auto;
      gap: 8px;
      min-height: 104px;
      padding: 10px;
      border-radius: 8px;
      background:
        linear-gradient(180deg, var(--tint), rgba(255,255,255,0.32)),
        rgba(255, 255, 255, 0.55);
      border: 1px solid rgba(17,24,39,0.10);
      border-top: 3px solid var(--accent);
      box-shadow: 0 1px 4px rgba(0,0,0,0.10);
      backdrop-filter: blur(4px);
      color: #111;
    }

    .metric-card:hover {
      background:
        linear-gradient(180deg, var(--tint), rgba(255,255,255,0.46)),
        rgba(255,255,255,0.68);
    }

    @media (prefers-color-scheme: dark) {
      .metric-card {
        background:
          linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03)),
          rgba(40, 40, 46, 0.85);
        color: #f0f0f0;
        border-color: rgba(255,255,255,0.12);
      }

      .metric-card:hover {
        background:
          linear-gradient(180deg, rgba(255,255,255,0.11), rgba(255,255,255,0.05)),
          rgba(55, 55, 62, 0.88);
      }
    }

    .metric-card--biomass {
      --accent: #16a34a;
      --tint: rgba(187, 247, 208, 0.48);
      --icon-bg: rgba(187, 247, 208, 0.95);
      --icon-color: #14532d;
    }

    .metric-card--carbon {
      --accent: #64748b;
      --tint: rgba(226, 232, 240, 0.56);
      --icon-bg: rgba(226, 232, 240, 0.96);
      --icon-color: #334155;
    }

    .metric-card--oxygen {
      --accent: #0284c7;
      --tint: rgba(186, 230, 253, 0.52);
      --icon-bg: rgba(186, 230, 253, 0.95);
      --icon-color: #075985;
    }

    .metric-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 7px;
      min-width: 0;
    }

    .metric-name {
      min-width: 0;
      font-size: 0.76rem;
      font-weight: 800;
      line-height: 1.05;
    }

    .metric-icon {
      display: inline-grid;
      place-items: center;
      width: 28px;
      height: 28px;
      border-radius: 7px;
      flex: 0 0 auto;
      color: var(--icon-color);
      background: var(--icon-bg);
      box-shadow: inset 0 0 0 1px rgba(255,255,255,0.45);
    }

    .metric-icon svg {
      width: 18px;
      height: 18px;
      display: block;
    }

    .label {
      font-size: 0.7rem;
      opacity: .78;
      line-height: 1.2;
    }

    .metric-main {
      display: grid;
      gap: 7px;
    }

    .total {
      display: flex;
      align-items: baseline;
      gap: 4px;
      min-width: 0;
    }

    .meta-label {
      font-size: 0.58rem;
      font-weight: 800;
      text-transform: uppercase;
      opacity: 0.58;
    }

    .average {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 6px;
      padding: 5px 7px;
      border-radius: 7px;
      background: rgba(255,255,255,0.46);
    }

    @media (prefers-color-scheme: dark) {
      .average {
        background: rgba(255,255,255,0.08);
      }
    }

    .num {
      font-variant-numeric: tabular-nums lining-nums;
      font-feature-settings: "tnum","lnum";
      font-size: 0.82rem;
      font-weight: 800;
      line-height: 1.15;
      white-space: nowrap;
    }

    .num--total {
      font-size: 1rem;
    }

    .num--average {
      font-size: 0.78rem;
    }

    .unit {
      font-size: 0.58rem;
      font-weight: 700;
      opacity: 0.68;
      white-space: nowrap;
    }

    @media (max-width: 700px) {
      .legend {
        width: min(620px, calc(100vw - 20px));
        padding: 7px;
        font-size: 0.68rem;
        overflow-x: auto;
      }

      .metrics {
        grid-template-columns: repeat(3, minmax(180px, 1fr));
      }

      .metric-card {
        min-height: 100px;
      }

      .num {
        font-size: 0.7rem;
      }
    }
    `

    this._rootEl = document.createElement('div')
    this._rootEl.className = 'legend'
    this._shadow.append(style, this._rootEl)
  }

  _renderContent() {
    if (!this._rootEl) return

    this._rootEl.innerHTML = `
      <div class="legend-head">
        <div class="title">Impacto ambiental</div>
        <div class="count" title="Árboles con biomasa calculada">
          <span class="label">Árboles con biomasa</span>
          <span class="num">${this._formatValue(this.options.treesWithBiomass)}</span>
        </div>
      </div>
      <div class="metrics">
        ${this._renderMetricRow('Biomasa', 'biomass', this.options.totalBiomass, this.options.averageBiomass)}
        ${this._renderMetricRow('Carbono', 'carbon', this.options.totalCarbon, this.options.averageCarbon)}
        ${this._renderMetricRow('Oxígeno', 'oxygen', this.options.totalOxygen, this.options.averageOxygen)}
      </div>
    `
    this._applyVisibility()
  }

  _renderMetricRow(label, icon, total, average) {
    return `
      <div class="metric-card metric-card--${this._escape(icon)}" title="${this._escape(label)}">
        <span class="metric-top">
          <span class="metric-name">
            <span>${this._escape(label)}</span>
          </span>
          <span class="metric-icon" aria-hidden="true">
            ${this._icon(icon)}
          </span>
        </span>
        <span class="metric-main">
          <span class="total">
            <span class="meta-label">Total</span>
            <span class="num num--total">${this._formatValue(total, 2)}</span>
            <span class="unit">kg</span>
          </span>
          <span class="average">
            <span class="meta-label">Promedio</span>
            <span>
              <span class="num num--average">${this._formatValue(average, 2)}</span>
              <span class="unit">kg/árbol</span>
            </span>
          </span>
        </span>
      </div>
    `
  }

  _normalizeMetadata(input = {}) {
    const source = input?.metadata || input?.meta || input || {}

    const totalBiomass = this._numberFrom(source.totalBiomass, null)
    const averageBiomass = this._numberFrom(source.averageBiomass, null)
    const totalCarbon = totalBiomass === null ? null : totalBiomass * 0.5
    const averageCarbon = averageBiomass === null ? null : averageBiomass * 0.5
    const totalOxygen = totalCarbon === null ? null : (totalCarbon * 32) / 12
    const averageOxygen = averageCarbon === null ? null : (averageCarbon * 32) / 12

    return {
      totalBiomass,
      averageBiomass,
      totalCarbon,
      averageCarbon,
      totalOxygen,
      averageOxygen,
      treesWithBiomass: this._numberFrom(source.treesWithBiomass, null),
    }
  }

  _numberFrom(value, fallback = null) {
    if (value === null || value === undefined || value === '') return fallback
    const number = Number(value)
    return Number.isFinite(number) ? number : fallback
  }

  _fmt(n) {
    try {
      return new Intl.NumberFormat('es-EC', { maximumFractionDigits: 0 }).format(n)
    } catch {
      return String(n)
    }
  }

  _fmtDecimal(n, maximumFractionDigits = 2) {
    try {
      return new Intl.NumberFormat('es-EC', {
        minimumFractionDigits: 0,
        maximumFractionDigits,
      }).format(n)
    } catch {
      return String(n)
    }
  }

  _formatValue(value, decimals = 0) {
    if (value === null || value === undefined) return '--'
    return decimals > 0 ? this._fmtDecimal(value, decimals) : this._fmt(value)
  }

  _icon(name) {
    const icons = {
      biomass: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M20 4c-7.5 0-13 4.2-13 9.7 0 3 2.3 5.3 5.2 5.3C17.2 19 20 12.5 20 4Z"/><path d="M7 20c2.6-4.5 6.1-7.3 10.4-9.4"/></svg>',
      carbon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 20 7.5v9L12 21l-8-4.5v-9L12 3Z"/><path d="M14.6 9.2a4 4 0 1 0 0 5.6"/></svg>',
      oxygen: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="12" r="5"/><path d="M16 16.5h4"/><path d="M18 14.5c1.2 0 2 .8 2 1.8 0 1.6-2 2.4-4 4.2h4"/></svg>',
    }
    return icons[name] || ''
  }

  _escape(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }
}

export default BiomassLegendControl
